import { X } from 'lucide-react-native';
import { useCallback, useEffect } from 'react';

import { StatementTriagePhases } from './StatementTriagePhases';
import { pickPhaseTitle } from './statementTriageWizard';
import { useProvidePublishedFeedback } from './useProvidePublishedFeedback';
import { useProvideReferencePublish } from '../../hooks/useProvideReferencePublish';
import { useStatementTriageSheet } from '../../hooks/useStatementTriageSheet';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../../model/extractedStatements';
import type { PublishProvidedStatementReferencesResult } from '../../model/providedReferences';
import { deleteProvideReferenceDraftStore } from '../../state/provideReferenceDraftStore';
import { getStatementTriageStore } from '../../state/statementTriageStore';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Modal, ModalBackdrop, ModalCloseButton, ModalContent } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useCloseOnBrowserBack } from '@/hooks/useCloseOnBrowserBack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

interface StatementTriageSheetProps {
  conversationId: string;
  messageSid: string;
  extractedStatements: ExtractedStatement[];
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  attachedStatementReferences: readonly AttachedStatementReference[];
  /** Currently-persisted per-statement free-text help requests on the
   * parent message. */
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
}

/**
 * Web wrapper for the reference-request wizard. Three-phase modal that
 * walks the expert through select → searching → review inside a single
 * overlay. The phase tree itself lives in the shared
 * `StatementTriagePhases` (in `statementTriageWizard.tsx`); this file
 * owns only the Modal chrome, the header, and the Escape-to-close
 * affordance. Mirrors `StatementTriageSheet.tsx` (native) shape so the
 * calling site imports a single `StatementTriageSheet` symbol and Metro
 * picks the right file per platform.
 */
export function StatementTriageSheet({
  conversationId,
  messageSid,
  extractedStatements,
  statementCitationStatuses,
  attachedStatementReferences,
  statementReferenceHelpRequests,
}: StatementTriageSheetProps) {
  const statementCount = extractedStatements.length;

  const {
    isOpen,
    phase,
    entryMode,
    provideStatementIndices,
    activeCitationRequest,
    isSaving,
    errorMessage,
    isAutoStartPending,
    handleAutoStart,
    handleDismiss,
  } = useStatementTriageSheet({
    conversationId,
    messageSid,
    statementCount,
    statementCitationStatuses,
    currentAttachedStatementReferences: attachedStatementReferences,
  });

  /**
   * Close the wizard after a successful provide publish. Deliberately
   * does NOT route through `handleDismiss`: a completed publish already
   * fired `PROVIDE_REFERENCES_PUBLISHED`, so reusing the dismiss path
   * would also emit `PROVIDE_REFERENCES_DISMISSED` (with a reset, empty
   * draft) and double-count the same session in the funnel.
   */
  const confirmProvidePublished = useProvidePublishedFeedback();

  const handleProvidePublished = useCallback(
    (result: PublishProvidedStatementReferencesResult) => {
      confirmProvidePublished(result);

      getStatementTriageStore(messageSid).getState().close();
    },
    [confirmProvidePublished, messageSid]
  );

  // Evict the per-message provide draft store when this sheet unmounts
  // (its bubble scrolled out of the virtualized list, or the
  // conversation closed) so reset-but-retained stores don't accumulate
  // in the registry across a long inbox session. While the sheet stays
  // mounted the store instance is kept, but it does NOT carry drafts
  // across a close→reopen: `openProvideReferenceWizard` resets the draft
  // store on every open so a prior session's values never bleed in. The
  // eviction here is purely registry hygiene, not draft lifecycle.
  useEffect(
    () => () => {
      deleteProvideReferenceDraftStore(messageSid);
    },
    [messageSid]
  );

  const {
    canPublish: canPublishProvide,
    isPublishing: isProvidePublishing,
    errorMessage: provideErrorMessage,
    handlePublish: handleProvidePublish,
  } = useProvideReferencePublish({
    conversationId,
    messageSid,
    statementIndices: provideStatementIndices,
    currentReferences: attachedStatementReferences,
    currentHelpRequests: statementReferenceHelpRequests,
    currentStatementCitationStatuses: statementCitationStatuses,
    onPublished: handleProvidePublished,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving && !isProvidePublishing) {
        handleDismiss();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, isSaving, isProvidePublishing, handleDismiss]);

  const isBusy = isSaving || isProvidePublishing;

  const dismissUnlessBusy = useCallback(() => {
    if (!isBusy) {
      handleDismiss();
    }
  }, [isBusy, handleDismiss]);

  useCloseOnBrowserBack(isOpen, dismissUnlessBusy);

  const title = pickPhaseTitle(phase);

  return (
    <Modal isOpen={isOpen} onClose={handleDismiss} size="lg">
      <ModalBackdrop />
      <ModalContent
        testID="citation-wizard-screen"
        className="w-full max-w-2xl overflow-hidden"
        aria-label={title}>
        <VStack className="min-h-0 w-full flex-1">
          <HStack className="w-full items-center justify-between px-4 pt-4">
            <Text className="font-heading text-lg text-typography-900">{title}</Text>
            <ModalCloseButton
              disabled={isSaving || isProvidePublishing}
              hitSlop={ICON_BUTTON_HIT_SLOP}
              className="rounded-full p-2 web:hover:bg-background-50"
              {...getAccessibilityProps({ label: 'Close', role: 'button' })}
              accessibilityState={{ disabled: isSaving || isProvidePublishing }}>
              <Icon as={X} className="text-typography-700" size="md" />
            </ModalCloseButton>
          </HStack>
          <StatementTriagePhases
            conversationId={conversationId}
            messageSid={messageSid}
            extractedStatements={extractedStatements}
            attachedStatementReferences={attachedStatementReferences}
            statementReferenceHelpRequests={statementReferenceHelpRequests}
            phase={phase}
            activeCitationRequest={activeCitationRequest}
            isSaving={isSaving}
            errorMessage={errorMessage}
            isAutoStartPending={isAutoStartPending}
            onRetry={handleAutoStart}
            handleDismiss={handleDismiss}
            entryMode={entryMode}
            provide={{
              statementIndices: provideStatementIndices,
              isPublishing: isProvidePublishing,
              errorMessage: provideErrorMessage,
              canPublish: canPublishProvide,
              onPublish: handleProvidePublish,
            }}
          />
        </VStack>
      </ModalContent>
    </Modal>
  );
}
